import { useState } from "react";
import { AlertTriangle, Check, Copy } from "lucide-react";

import type { TenantProvisionResponse } from "@/api";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/i18n";
import { formatDateID } from "@/lib/format";

interface Props {
  result: TenantProvisionResponse | null;
  onClose: () => void;
}

/**
 * Post-provision dialog. Carries the admin's temporary password —
 * irretrievable after dismissal. Mirrors the signup-key + API-key
 * show-once UX so the super-admin can copy the credential pair once
 * before handing it off.
 */
export function TenantProvisionShowOnceDialog({ result, onClose }: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  if (!result) return null;

  const handleCopy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard blocked — let the user copy manually.
    }
  };

  const handleClose = () => {
    setCopied(null);
    setAcknowledged(false);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-lg" modal>
      <DialogHeader>
        <DialogTitle>
          <span className="inline-flex items-center gap-2">
            <Check size={18} className="text-success-600" />
            {t("platform.tenants.provisionResult.title")}
          </span>
        </DialogTitle>
        <DialogDescription>
          {t("platform.tenants.provisionResult.description", {
            tenant: result.tenant_name,
          })}
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="rounded-md border border-ocean-100 bg-ocean-50/40 px-4 py-3">
          <div className="label-eyebrow mb-2">
            {t("platform.tenants.provisionResult.emailLabel")}
          </div>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 break-all rounded border border-ocean-100 bg-white px-3 py-2 font-mono text-sm text-ocean-900">
              {result.admin_email}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleCopy("email", result.admin_email)}
              className="shrink-0"
            >
              {copied === "email" ? (
                <>
                  <Check size={14} className="text-success-600" />
                  {t("platform.tenants.provisionResult.copied")}
                </>
              ) : (
                <>
                  <Copy size={14} />
                  {t("platform.tenants.provisionResult.copy")}
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="rounded-md border border-ocean-100 bg-ocean-50/40 px-4 py-3">
          <div className="label-eyebrow mb-2">
            {t("platform.tenants.provisionResult.passwordLabel")}
          </div>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 break-all rounded border border-ocean-100 bg-white px-3 py-2 font-mono text-sm text-ocean-900">
              {result.admin_temp_password}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                handleCopy("password", result.admin_temp_password)
              }
              className="shrink-0"
            >
              {copied === "password" ? (
                <>
                  <Check size={14} className="text-success-600" />
                  {t("platform.tenants.provisionResult.copied")}
                </>
              ) : (
                <>
                  <Copy size={14} />
                  {t("platform.tenants.provisionResult.copy")}
                </>
              )}
            </Button>
          </div>
        </div>

        <Alert variant="warning">
          <div className="flex items-start gap-2 text-xs">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <strong>
                {t("platform.tenants.provisionResult.warningHeading")}
              </strong>{" "}
              {t("platform.tenants.provisionResult.warningBody")}
            </div>
          </div>
        </Alert>

        <div className="grid grid-cols-1 gap-1 rounded-md bg-neutral-50 px-3 py-2 text-xs text-neutral-600 tablet:grid-cols-2">
          <div>
            <span className="font-medium text-neutral-800">
              {t("platform.tenants.provisionResult.trialEndsLabel")}:
            </span>{" "}
            {formatDateID(result.trial_expires_at)}
          </div>
          <div>
            <span className="font-medium text-neutral-800">
              {t("platform.tenants.provisionResult.slugLabel")}:
            </span>{" "}
            <code className="font-mono">{result.tenant_slug}</code>
          </div>
        </div>

        <label className="flex items-start gap-2 text-xs text-neutral-700">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-ocean-200 text-ocean-600 focus:ring-ocean-400"
          />
          <span>{t("platform.tenants.provisionResult.acknowledge")}</span>
        </label>
      </DialogBody>

      <DialogFooter>
        <Button type="button" onClick={handleClose} disabled={!acknowledged}>
          {t("platform.tenants.provisionResult.closeButton")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
